import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { trackVendorSignupComplete } from "@/services/metaPixel";
import {
  User,
  Phone,
  MapPin,
  ChevronRight,
  Loader2,
  Home,
  AlertCircle,
} from "lucide-react";

export default function RegistroDatos() {
  const navigate = useNavigate();
  const [tipoVendedor, setTipoVendedor] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    barrio: "",
    direccion: "", // Solo para carrito
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check if user came from previous steps
    const method = sessionStorage.getItem('registro_method');
    const tipo = sessionStorage.getItem('registro_tipo');
    
    if (!method || !tipo) {
      navigate("/registro-vendedor/tipo");
      return;
    }
    
    setTipoVendedor(tipo);
    
    const nombreGoogle = sessionStorage.getItem('registro_nombre');
    if (nombreGoogle) {
      setForm((f) => ({ ...f, nombre: f.nombre || nombreGoogle }));
    }
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.nombre.trim()) {
      newErrors.nombre = "Nombre completo requerido";
    } else if (form.nombre.trim().split(" ").length < 2) {
      newErrors.nombre = "Ingresa nombre y apellido";
    }
    
    if (!form.telefono.trim()) {
      newErrors.telefono = "Celular requerido";
    } else if (!/^[0-9]{10}$/.test(form.telefono.replace(/\s/g, ""))) {
      newErrors.telefono = "Celular debe tener 10 dígitos";
    }
    
    if (!form.barrio.trim()) {
      newErrors.barrio = "Barrio o localidad requerido";
    }
    
    // Dirección solo requerida para carrito
    if (tipoVendedor === "carrito" && !form.direccion.trim()) {
      newErrors.direccion = "Dirección del punto de venta requerida";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    
    try {
      const email = sessionStorage.getItem('registro_email');
      const password = sessionStorage.getItem('registro_password');
      const method = sessionStorage.getItem('registro_method');
      
      if (!email) {
        toast.error("No encontramos tu correo. Vuelve a elegir cómo registrarte.");
        setSubmitting(false);
        navigate("/registro-vendedor/acceso");
        return;
      }
      
      // Create registration payload
      const payload = {
        nombre: form.nombre.trim(),
        email,
        telefono: form.telefono.replace(/\s/g, ""),
        direccion: form.direccion || form.barrio, // Use barrio if no direccion
        zona: form.barrio,
        tipo_vendedor: tipoVendedor,
        ...(password ? { password } : {}),
        notas: `Registro desde nuevo onboarding. Método: ${method}`,
        // New fields for the registration flow
        barrio: form.barrio,
        estado_registro: "pendiente_aprobacion",
        metodo_registro: method,
      };
      
      await api.post("/registro/solicitud", payload);
      
      // Track vendor signup completion
      trackVendorSignupComplete(tipoVendedor, form.barrio);
      
      // Clear session storage
      sessionStorage.removeItem('registro_method');
      sessionStorage.removeItem('registro_email');
      sessionStorage.removeItem('registro_password');
      sessionStorage.removeItem('registro_tipo');
      
      // Store name for the waiting screen
      sessionStorage.setItem('registro_nombre', form.nombre.split(" ")[0]);
      
      navigate("/registro-vendedor/espera");
      
    } catch (error) {
      console.error("Registration error:", error);
      const message = error.response?.data?.detail || "Error al enviar solicitud";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!tipoVendedor) return null;

  const isDigital = tipoVendedor === "digital";

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#4A2C0A] font-bold">
          Completa tus datos
        </h1>
        <p className="font-['Poppins'] text-[#4A2C0A]/70">
          {isDigital 
            ? "Solo necesitamos unos pocos datos para continuar"
            : "Datos de contacto y ubicación de tu punto de venta"
          }
        </p>
      </div>

      {/* Type Badge */}
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium font-['Poppins'] ${
        isDigital 
          ? "bg-blue-100 text-blue-700" 
          : "bg-[#FEF3E2] text-[#C8902A]"
      }`}>
        {isDigital ? "📱 Vendedor Digital" : "🛒 Vendedor Carrito"}
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl p-6 space-y-5 shadow-sm">
        {/* Nombre */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#4A2C0A]/80 flex items-center gap-1">
            Nombre completo
            <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C0A]/40" />
            <Input
              placeholder="Ej: Juan Carlos Pérez"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className={`pl-10 h-12 rounded-xl border-[#E5D4B3] focus:border-[#C8902A] focus:ring-[#C8902A]/20 ${
                errors.nombre ? "border-red-400" : ""
              }`}
              data-testid="input-nombre"
            />
          </div>
          {errors.nombre && (
            <p className="text-xs text-red-500 font-['Poppins'] flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.nombre}
            </p>
          )}
        </div>

        {/* Celular */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#4A2C0A]/80 flex items-center gap-1">
            Celular
            <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C0A]/40" />
            <Input
              type="tel"
              placeholder="300 123 4567"
              value={form.telefono}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/[^\d\s]/g, "");
                setForm({ ...form, telefono: value });
              }}
              className={`pl-10 h-12 rounded-xl border-[#E5D4B3] focus:border-[#C8902A] focus:ring-[#C8902A]/20 ${
                errors.telefono ? "border-red-400" : ""
              }`}
              data-testid="input-telefono"
            />
          </div>
          {errors.telefono && (
            <p className="text-xs text-red-500 font-['Poppins'] flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.telefono}
            </p>
          )}
        </div>

        {/* Barrio / Localidad */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-[#4A2C0A]/80 flex items-center gap-1">
            Barrio / Localidad
            <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C0A]/40" />
            <Input
              placeholder="Ej: Chapinero, Kennedy, etc."
              value={form.barrio}
              onChange={(e) => setForm({ ...form, barrio: e.target.value })}
              className={`pl-10 h-12 rounded-xl border-[#E5D4B3] focus:border-[#C8902A] focus:ring-[#C8902A]/20 ${
                errors.barrio ? "border-red-400" : ""
              }`}
              data-testid="input-barrio"
            />
          </div>
          {errors.barrio && (
            <p className="text-xs text-red-500 font-['Poppins'] flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {errors.barrio}
            </p>
          )}
        </div>

        {/* Dirección - Solo para carrito */}
        {!isDigital && (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-[#4A2C0A]/80 flex items-center gap-1">
              Dirección del punto de venta
              <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C0A]/40" />
              <Input
                placeholder="Calle, número, referencias..."
                value={form.direccion}
                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                className={`pl-10 h-12 rounded-xl border-[#E5D4B3] focus:border-[#C8902A] focus:ring-[#C8902A]/20 ${
                  errors.direccion ? "border-red-400" : ""
                }`}
                data-testid="input-direccion"
              />
            </div>
            {errors.direccion && (
              <p className="text-xs text-red-500 font-['Poppins'] flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.direccion}
              </p>
            )}
            <p className="text-xs text-[#4A2C0A]/50 font-['Poppins']">
              La ubicación donde operará tu carrito de ventas
            </p>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 rounded-xl p-4 flex gap-3">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <p className="text-sm text-blue-800 font-['Poppins']">
            <strong>Sin datos bancarios por ahora</strong>
          </p>
          <p className="text-xs text-blue-600 font-['Poppins'] mt-0.5">
            Los datos bancarios los completarás después de ser aprobado, en tu primer ingreso.
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full h-14 text-lg font-semibold bg-[#C8902A] hover:bg-[#A67720] text-white rounded-2xl shadow-lg shadow-[#C8902A]/30 font-['Poppins']"
        data-testid="btn-enviar-solicitud"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            Enviar solicitud
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
}
